import { useCallback, useEffect, useRef, useState } from 'react';

import type { HeroVariant, PresenceHeroContent } from '~/marketing/presenceContract';
import type { MarketingDoorId } from '~/marketing/landingContent';
import {
  captureEvent,
  getResolvedHeroVariant,
  ensurePosthogIdentify,
  initPosthogOnce,
  isLandingMotionKilled,
  isPosthogEnabled,
  isPresenceApiKilled,
  presenceEvents,
  posthog,
} from './posthogPresence';
import { fetchActivity, presenceApiEnabled } from './usePresenceApi';
import { usePresenceBootstrap } from './usePresenceBootstrap';
import {
  mergeHeroContent,
  mergeBootstrap,
  DEFAULT_RESPONSES,
  GENERIC_ACTIVITY_FALLBACK,
} from './presenceStaticHero';
import { useAnonId } from './useAnonId';
import { useReducedMotion } from './useReducedMotion';

function readCityParam(): string {
  if (typeof window === 'undefined') return 'austin';
  const c = new URLSearchParams(window.location.search).get('city')?.trim();
  return c && c.length > 0 ? c : 'austin';
}

/**
 * Wires PostHog flags, optional presence API bootstrap, and merged hero/activity for the marketing page.
 * `door` (two-door campaign) rides every presence event — URL-forced doors are non-random, so
 * without the dimension they would silently contaminate the hero A/B variant metrics.
 */
export function useMarketingPresence(options?: { door?: MarketingDoorId | null }) {
  const door = options?.door ?? null;
  const doorDim = door ?? 'default';
  const anonId = useAnonId();
  const [city, setCity] = useState('austin');
  const [flagsReady, setFlagsReady] = useState(!isPosthogEnabled());
  const [variant, setVariant] = useState<HeroVariant>('control');
  const [activityLine, setActivityLine] = useState<string | undefined>(undefined);
  const userRm = useReducedMotion();

  useEffect(() => {
    setCity(readCityParam());
  }, []);

  const assignedPh = useRef(false);
  useEffect(() => {
    if (!isPosthogEnabled()) return;
    initPosthogOnce(anonId);
    posthog.onFeatureFlags?.(() => {
      const v = getResolvedHeroVariant();
      setVariant(v);
      setFlagsReady(true);
      if (!assignedPh.current) {
        assignedPh.current = true;
        captureEvent(presenceEvents.hero_variant_assigned, {
          variant: v,
          city: readCityParam(),
          assignment_source: 'posthog',
          anon_id: anonId,
          door: doorDim,
        });
      }
    });
    const t = window.setTimeout(() => setFlagsReady(true), 3000);
    return () => {
      window.clearTimeout(t);
    };
  }, [anonId, doorDim]);

  const apiKilled = flagsReady && isPosthogEnabled() ? isPresenceApiKilled() : false;
  const motionKilled = flagsReady && isPosthogEnabled() ? isLandingMotionKilled() : false;
  const useApi = isPosthogEnabled() && !apiKilled && presenceApiEnabled() && flagsReady;

  const { bootstrap, status: bootstrapStatus } = usePresenceBootstrap({
    enabled: useApi,
    city,
  });

  useEffect(() => {
    const sid = bootstrap?.anonId?.trim();
    if (sid) ensurePosthogIdentify(sid);
  }, [bootstrap?.anonId]);

  mergeBootstrap(bootstrap, variant);
  const hero: PresenceHeroContent = (() => {
    let h = mergeHeroContent(variant, bootstrap?.hero);
    if (variant === 'A' && (!h.responses || h.responses.length < 1)) {
      h = { ...h, responses: DEFAULT_RESPONSES };
    }
    return h;
  })();

  useEffect(() => {
    if (variant !== 'C') {
      setActivityLine(undefined);
      return;
    }
    if (!useApi) {
      setActivityLine(hero.activityLine ?? GENERIC_ACTIVITY_FALLBACK);
      return;
    }
    let cancelled = false;
    void fetchActivity(city).then((a) => {
      if (cancelled) return;
      if (a?.line) setActivityLine(a.line);
      else setActivityLine(hero.activityLine ?? GENERIC_ACTIVITY_FALLBACK);
    });
    return () => {
      cancelled = true;
    };
  }, [variant, useApi, city, hero.activityLine]);

  const disableAsideFloat = motionKilled || userRm;
  const isLoading = useApi && bootstrapStatus === 'loading';

  const onLandingView = useCallback(() => {
    captureEvent(presenceEvents.landing_page_view, {
      path: typeof window !== 'undefined' ? window.location.pathname : '/',
      city,
      anon_id: anonId,
      door: doorDim,
    });
  }, [anonId, city, doorDim]);

  const assignedNoPh = useRef(false);
  useEffect(() => {
    if (isPosthogEnabled() || assignedNoPh.current) return;
    assignedNoPh.current = true;
    captureEvent(presenceEvents.hero_variant_assigned, {
      variant: 'control',
      city: readCityParam(),
      assignment_source: 'static',
      anon_id: anonId,
      door: doorDim,
    });
  }, [anonId, doorDim]);

  const onHeroInView = useCallback(() => {
    captureEvent(presenceEvents.hero_variant_viewed, {
      variant,
      city,
      slot: 'hero',
      anon_id: anonId,
      door: doorDim,
    });
  }, [variant, city, anonId, doorDim]);

  const onPrimaryCtaClick = useCallback(() => {
    captureEvent(presenceEvents.hero_primary_cta_click, {
      variant,
      cta_label: hero.primaryCta.label,
      target_href: hero.primaryCta.href,
      city,
      anon_id: anonId,
      door: doorDim,
    });
  }, [variant, hero.primaryCta, city, anonId, doorDim]);

  // Only reachable when a hero actually has a secondary CTA. The static heroes dropped theirs, so
  // today this fires only for a server-supplied one — the event stays wired rather than deleted,
  // because the presence API can still send it.
  const onSecondaryCtaClick = useCallback(() => {
    const cta = hero.secondaryCta;
    if (!cta) return;
    captureEvent(presenceEvents.hero_secondary_cta_click, {
      variant,
      cta_label: cta.label,
      target_href: cta.href,
      city,
      anon_id: anonId,
      door: doorDim,
    });
  }, [variant, hero.secondaryCta, city, anonId, doorDim]);

  return {
    city,
    anonId,
    flagsReady,
    variant,
    hero,
    activityLine:
      activityLine ??
      (variant === 'C' ? (hero.activityLine ?? GENERIC_ACTIVITY_FALLBACK) : undefined),
    motionKilled,
    userReducedMotion: userRm,
    disableAsideFloat,
    isLoading,
    useApi,
    onLandingView,
    onHeroInView,
    onPrimaryCtaClick,
    onSecondaryCtaClick,
  };
}
